import React from 'react';
import { Link } from 'react-router-dom';
import { FiPlay, FiActivity, FiShoppingCart, FiCheckCircle, FiWatch, FiMessageSquare } from 'react-icons/fi';
import NavSearch from './NavSearch';
import AccountMenu from './AccountMenu';
import PinnedQuickActions from './PinnedQuickActions';

// Desktop slim top bar — always visible regardless of sidebar collapsed state.
// Pinned, left to right: search, Start/Stop Production, Orders, QA Review, the user's
// customizable shortcuts, Clock terminal, Messaging, account menu. Left-offset matches
// the sidebar's current width.
export default function TopBar({
  sidebarCollapsed,
  accessibleItems,
  badgeValues,
  onOpenChat,
  production, // { canProduction, activeRunId, checkingActiveRun, livePath }
  orders,     // { visible, target, badgeCount }
  qaReview,   // { visible, badgeCount }
  clock,      // { visible, href }
  totalUnread,
}) {
  return (
    <div
      className="app-chrome d-none d-lg-flex align-items-center"
      style={{
        position: 'fixed', top: 0, right: 0,
        left: sidebarCollapsed ? 'var(--sidebar-width-collapsed)' : 'var(--sidebar-width-expanded)',
        height: 'var(--topbar-height)',
        background: '#1b2638',
        zIndex: 999,
        padding: '0 16px',
        gap: 10,
        transition: 'left 0.15s ease',
      }}
    >
      <NavSearch variant="topbar" accessibleItems={accessibleItems} onOpenChat={onOpenChat} />

      {production.canProduction && (
        <Link
          className={`btn btn-sm ${production.activeRunId ? 'btn-danger' : 'btn-success'} d-flex align-items-center gap-2`}
          to={production.livePath}
          title={production.activeRunId ? `Open live run #${production.activeRunId}` : 'Start a new production run'}
        >
          {production.activeRunId ? <FiActivity size={14} /> : <FiPlay size={14} />}
          {production.activeRunId ? (
            <>Production in Progress <span className="badge bg-light text-dark ms-1">RUN #{production.activeRunId}</span></>
          ) : (
            <>Start Production{production.checkingActiveRun && <span className="spinner-border spinner-border-sm ms-2" />}</>
          )}
        </Link>
      )}

      {orders.visible && (
        <Link className="btn btn-sm btn-warning position-relative d-flex align-items-center gap-2" to={orders.target}>
          <FiShoppingCart size={14} /> Orders
          {orders.badgeCount > 0 && (
            <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: 10 }}>
              {orders.badgeCount > 99 ? '99+' : orders.badgeCount}
            </span>
          )}
        </Link>
      )}

      {qaReview.visible && (
        <Link className="btn btn-sm btn-info position-relative d-flex align-items-center gap-2" to="/ccp/baking/queue">
          <FiCheckCircle size={14} /> QA Review
          {qaReview.badgeCount > 0 && (
            <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: 10 }}>
              {qaReview.badgeCount > 99 ? '99+' : qaReview.badgeCount}
            </span>
          )}
        </Link>
      )}

      <PinnedQuickActions accessibleItems={accessibleItems} badgeValues={badgeValues} />

      <div className="ms-auto d-flex align-items-center gap-2">
        {clock.visible && (
          <a href={clock.href} target="_blank" rel="noreferrer" title="Open Clock Terminal" className="btn btn-sm btn-outline-light">
            <FiWatch size={16} />
          </a>
        )}
        {onOpenChat && (
          <button className="btn btn-sm btn-outline-light position-relative" onClick={onOpenChat} title="Team Messages">
            <FiMessageSquare size={16} />
            {totalUnread > 0 && (
              <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: 10 }}>
                {totalUnread > 99 ? '99+' : totalUnread}
              </span>
            )}
          </button>
        )}
        <AccountMenu />
      </div>
    </div>
  );
}
